/**
 * AP-36 7.3 — Werte vergleichen, ohne dass Darstellungsunterschiede einen
 * Konflikt erzeugen: `null`, `undefined` und `''` sind „leer", `"12.5000"`
 * (Decimal aus Prisma) ist `12.5`, ein Zeitpunkt ist ein Zeitpunkt und ein
 * Datum ohne Uhrzeit ein Tag.
 */

type Normiert = null | string | number | boolean | { zeit: number } | { json: string }

const ISO_ZEIT = /^\d{4}-\d{2}-\d{2}T/
const TAG = /^\d{4}-\d{2}-\d{2}$/
const ZAHL = /^-?\d+(\.\d+)?$/

function stabil(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stabil).join(',')}]`
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stabil(o[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(v ?? null)
}

export function normiere(v: unknown): Normiert {
  if (v === undefined || v === null) return null
  if (v instanceof Date) return { zeit: v.getTime() }
  if (typeof v === 'string') {
    const t = v.trim()
    if (t === '') return null
    if (ISO_ZEIT.test(t)) {
      const zeit = Date.parse(t)
      if (!Number.isNaN(zeit)) return { zeit }
    }
    if (ZAHL.test(t)) return Number(t)
    return t
  }
  if (typeof v === 'number' || typeof v === 'boolean') return v
  if (Array.isArray(v) || typeof v === 'object') return { json: stabil(v) }
  return String(v)
}

/** Tag (`2026-09-20`) gegen Zeitpunkt: gleich, wenn der Zeitpunkt auf diesen Tag fällt (UTC, wie gespeichert). */
function tagGegenZeit(tag: string, zeit: number): boolean {
  return new Date(zeit).toISOString().slice(0, 10) === tag
}

export function gleich(a: unknown, b: unknown): boolean {
  const x = normiere(a)
  const y = normiere(b)
  if (x === null || y === null) return x === y
  if (typeof x === 'object' && typeof y === 'object') {
    if ('zeit' in x && 'zeit' in y) return x.zeit === y.zeit
    if ('json' in x && 'json' in y) return x.json === y.json
    return false
  }
  if (typeof x === 'string' && TAG.test(x) && typeof y === 'object' && 'zeit' in y) return tagGegenZeit(x, y.zeit)
  if (typeof y === 'string' && TAG.test(y) && typeof x === 'object' && 'zeit' in x) return tagGegenZeit(y, x.zeit)
  return x === y
}

/** Anzeige eines Werts in der Konfliktfrage. */
export function alsText(v: unknown): string {
  const n = normiere(v)
  if (n === null) return '(leer)'
  if (typeof n === 'boolean') return n ? 'ja' : 'nein'
  if (typeof n === 'object' && 'zeit' in n) {
    const d = new Date(n.zeit)
    return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 ? d.toLocaleDateString('de-DE', { timeZone: 'UTC' }) : d.toLocaleString('de-DE')
  }
  if (typeof n === 'object') return '(geändert)'
  return String(n)
}
